<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import categoryService from '../services/category.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t } = useI18n()

const categoryUuid = route.params.uuid as string
const category = ref<any>(null)
const coaches = ref<any[]>([])
const players = ref<any[]>([])
const events = ref<any[]>([])
const loading = ref(false)
const loadingCoaches = ref(false)
const loadingPlayers = ref(false)
const loadingEvents = ref(false)
const error = ref<string | null>(null)

// Delete modal for events
const showWhatsappModal = ref(false)
const showDeleteModal = ref(false)
const eventToDelete = ref<any>(null)
const deleting = ref(false)

// Pagination for players
const currentPage = ref(1)
const perPage = ref(25)
const totalPlayers = ref(0)
const lastPage = ref(1)
const fromItem = ref(0)
const toItem = ref(0)

// Get club UUID from init data
const clubUuid = authStore.initData?.club?.uuid || null

// Date filters - default: -7 days to +14 days from today
const getDefaultDateFrom = () => {
  const date = new Date()
  date.setDate(date.getDate() - 7)
  return date.toISOString().split('T')[0]
}

const getDefaultDateTo = () => {
  const date = new Date()
  date.setDate(date.getDate() + 14)
  return date.toISOString().split('T')[0]
}

const dateFrom = ref(getDefaultDateFrom())
const dateTo = ref(getDefaultDateTo())
const displayDateFrom = ref('')
const displayDateTo = ref('')

// Dropdowns and accordions for events
const openDropdownId = ref<string | null>(null)
const openAccordionIds = ref<Set<number>>(new Set())

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canEdit = computed(() => hasPermission('CATEGORY_UPDATE'))
const canSeeEvents = computed(() => hasPermission('EVENT_INDEX'))
const canShowEvent = computed(() => hasPermission('EVENT_SHOW'))
const canUpdateEvent = computed(() => hasPermission('EVENT_UPDATE'))
const canDeleteEvent = computed(() => hasPermission('EVENT_DELETE'))
const canAttendanceStore = computed(() => hasPermission('EVENT_ATTENDANCE_STORE'))
const canAttendanceUpdate = computed(() => hasPermission('EVENT_ATTENDANCE_UPDATE'))
const canSeeActions = computed(() => canShowEvent.value || canUpdateEvent.value || canDeleteEvent.value || canAttendanceStore.value || canAttendanceUpdate.value)

// Get current role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user can click on category (OWNER, SUPERVISOR, COACH can)
const canClickCategory = computed(() => {
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR' || currentRole.value === 'COACH'
})

// Fetch category data
const fetchCategory = async () => {
  if (!clubUuid || !categoryUuid) {
    error.value = t('categoryDetail.errorNoClubOrCategory')
    return
  }

  loading.value = true

  try {
    console.log('Fetching category:', categoryUuid)
    const response = await categoryService.getCategory(clubUuid, categoryUuid)
    console.log('Category response:', response)
    category.value = response
  } catch (err: any) {
    console.error('Error fetching category:', err)
    error.value = err.response?.data?.message || t('categoryDetail.errorFetchingCategory')
  } finally {
    loading.value = false
  }
}

// Fetch coaches
const fetchCoaches = async () => {
  if (!clubUuid || !categoryUuid) return

  loadingCoaches.value = true

  try {
    console.log('Fetching coaches for category:', categoryUuid)
    const response = await categoryService.getCategoryCoaches(clubUuid, categoryUuid)
    console.log('Coaches response:', response)
    coaches.value = response
  } catch (err: any) {
    console.error('Error fetching coaches:', err)
  } finally {
    loadingCoaches.value = false
  }
}

// Fetch players
const fetchPlayers = async (page: number = 1) => {
  if (!clubUuid || !categoryUuid) return

  loadingPlayers.value = true

  try {
    console.log('Fetching players for category:', categoryUuid, 'page:', page)
    const response = await categoryService.getCategoryPlayers(clubUuid, categoryUuid, page, perPage.value)
    console.log('Players response:', response)
    players.value = response.data || []
    currentPage.value = response.current_page || 1
    totalPlayers.value = response.total || 0
    lastPage.value = response.last_page || 1
    fromItem.value = response.from || 0
    toItem.value = response.to || 0
  } catch (err: any) {
    console.error('Error fetching players:', err)
  } finally {
    loadingPlayers.value = false
  }
}

// Pagination handlers
const changePage = (page: number) => {
  if (page >= 1 && page <= lastPage.value) {
    currentPage.value = page
    fetchPlayers(page)
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Watch perPage changes
const changePerPage = () => {
  currentPage.value = 1
  fetchPlayers(1)
}

// Get coach display name
const getCoachName = (coach: any) => {
  const firstName = coach.first_name || ''
  const lastName = coach.last_name || ''
  return `${lastName} ${firstName}`.trim() || t('categoryDetail.noName')
}

// Get player display name
const getPlayerName = (player: any) => {
  const firstName = player.first_name || ''
  const lastName = player.last_name || ''
  return `${lastName} ${firstName}`.trim() || t('categoryDetail.noName')
}

// Fetch events
const fetchEvents = async () => {
  if (!clubUuid || !categoryUuid) return

  loadingEvents.value = true

  try {
    console.log('Fetching events for category:', categoryUuid)
    const response = await categoryService.getCategoryEvents(
      clubUuid,
      categoryUuid,
      dateFrom.value || undefined,
      dateTo.value || undefined
    )
    console.log('Events response:', response)
    events.value = response.data || []
    displayDateFrom.value = response.date_from || ''
    displayDateTo.value = response.date_to || ''
  } catch (err: any) {
    console.error('Error fetching events:', err)
  } finally {
    loadingEvents.value = false
  }
}

// Filter events
const filterEvents = () => {
  fetchEvents()
}

// Format date for display
const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Format time for display
const formatTime = (dateTimeStr: string) => {
  if (!dateTimeStr) return ''
  return dateTimeStr.split(' ')[1]?.substring(0, 5) || ''
}

// Day abbreviations
const getDayAbbreviation = (day: string) => {
  const days: Record<string, string> = {
    [t('events.days.monday')]: t('events.days.mondayShort'),
    [t('events.days.tuesday')]: t('events.days.tuesdayShort'),
    [t('events.days.wednesday')]: t('events.days.wednesdayShort'),
    [t('events.days.thursday')]: t('events.days.thursdayShort'),
    [t('events.days.friday')]: t('events.days.fridayShort'),
    [t('events.days.saturday')]: t('events.days.saturdayShort'),
    [t('events.days.sunday')]: t('events.days.sundayShort')
  }
  return days[day] || day
}

// Toggle dropdown
const toggleDropdown = (id: string, event: Event) => {
  event.stopPropagation()
  openDropdownId.value = openDropdownId.value === id ? null : id
}

// Close dropdowns
const closeDropdowns = () => {
  openDropdownId.value = null
}

// Toggle accordion for mobile
const toggleAccordion = (index: number) => {
  if (openAccordionIds.value.has(index)) {
    openAccordionIds.value.delete(index)
  } else {
    openAccordionIds.value.add(index)
  }
}

// Check if event is in last three
const isLastThree = (index: number) => {
  return index >= events.value.length - 3
}

// Delete handlers for events
const openDeleteModalFn = (event: any) => {
  eventToDelete.value = event
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  eventToDelete.value = null
}

const handleDelete = async () => {
  if (!eventToDelete.value || !clubUuid) return

  deleting.value = true

  try {
    // Import event service
    const eventService = (await import('../services/event.service')).default
    const { useToastStore } = await import('../stores/toast')
    const toastStore = useToastStore()

    // Extract date from start datetime
    const eventDate = eventToDelete.value.start?.split(' ')[0]
    const response = await eventService.deleteEvent(clubUuid, eventToDelete.value.uuid, eventDate)
    const message = response?.message || t('events.eventDeleted')
    toastStore.success(message)

    closeDeleteModal()
    fetchEvents()
  } catch (err: any) {
    console.error('Error deleting event:', err)
    const { useToastStore } = await import('../stores/toast')
    const toastStore = useToastStore()
    const errorMessage = err.response?.data?.message || t('events.errorDeletingEvent')
    toastStore.error(errorMessage)
  } finally {
    deleting.value = false
  }
}

// Close dropdowns when clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', closeDropdowns)
}

onMounted(() => {
  fetchCategory()
  fetchCoaches()
  fetchPlayers()
  if (canSeeEvents.value) {
    fetchEvents()
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/categories" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('categoryDetail.backToList') }}
        </router-link>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('categoryDetail.loading') }}</p>
      </div>

      <!-- Category Content -->
      <template v-else-if="category">
        <!-- Header with Actions -->
        <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ category.name }}</h1>
            <p class="text-sm text-gray-600">{{ t('categoryDetail.coachesCount') }} {{ category.coaches_count || 0 }} | {{ t('categoryDetail.playersCount') }} {{ category.players_count || 0 }}</p>
          </div>
          <div class="flex flex-col items-end gap-2">
            <router-link
              v-if="canEdit"
              :to="`/categories/${categoryUuid}/edit`"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600"
            >
              {{ t('categoryDetail.edit') }}
            </router-link>
            <button
              v-if="category.whatsapp_enabled && category.whatsapp_link"
              @click="showWhatsappModal = true"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600"
            >
              {{ t('categoryDetail.whatsappGroup') }}
            </button>
          </div>
        </div>

        <!-- WhatsApp Modal -->
        <div v-if="showWhatsappModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="showWhatsappModal = false">
          <div class="bg-white rounded shadow-lg p-6 max-w-sm w-full mx-4">
            <h2 class="text-base font-semibold text-gray-900 mb-4">{{ t('categoryDetail.whatsappGroupLink') }}</h2>
            <a
              :href="category.whatsapp_link"
              target="_blank"
              rel="noopener noreferrer"
              class="block text-green-600 font-medium hover:underline break-all mb-6"
              @click="showWhatsappModal = false"
            >{{ category.whatsapp_link }}</a>
            <div class="flex justify-end">
              <button
                @click="showWhatsappModal = false"
                class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
              >
                {{ t('common.cancel') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Single Container with Coaches and Players -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
          <!-- Coaches Section -->
          <div class="mb-6">
            <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('categoryDetail.coaches') }}</h2>

            <!-- Loading State for Coaches -->
            <div v-if="loadingCoaches" class="text-sm text-gray-500">
              {{ t('categoryDetail.loadingCoaches') }}
            </div>

            <!-- Coaches Grid (4 columns on large screens) -->
            <div v-else-if="coaches.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <div
                v-for="coach in coaches"
                :key="coach.uuid"
                class="p-3 border border-gray-200 rounded hover:bg-gray-50"
              >
                <router-link
                  :to="`/coaches/${coach.uuid}`"
                  class="text-sm font-medium text-blue-600 hover:underline"
                >
                  {{ getCoachName(coach) }}
                </router-link>
              </div>
            </div>

            <!-- No Coaches -->
            <div v-else class="text-sm text-gray-500">
              {{ t('categoryDetail.noCoaches') }}
            </div>
          </div>

          <!-- Players Section -->
          <div>
            <h2 class="text-base font-semibold text-gray-900 mb-3">{{ t('categoryDetail.players') }}</h2>

            <!-- Loading State for Players -->
            <div v-if="loadingPlayers" class="text-sm text-gray-500">
              {{ t('categoryDetail.loadingPlayers') }}
            </div>

            <!-- Players Grid (4 columns on large screens) -->
            <div v-else-if="players.length > 0" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <div
                  v-for="player in players"
                  :key="player.uuid"
                  class="p-3 border border-gray-200 rounded hover:bg-gray-50"
                >
                  <router-link
                    :to="`/players/${player.uuid}`"
                    class="text-sm font-medium text-blue-600 hover:underline"
                  >
                    {{ getPlayerName(player) }}
                  </router-link>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="totalPlayers > 0" class="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
                <div class="text-sm text-gray-700">
                  {{ t('categoryDetail.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('categoryDetail.of') }} {{ totalPlayers }} {{ t('categoryDetail.playersWord') }}
                </div>
                <div class="flex items-center gap-4">
                  <div class="flex items-center gap-2">
                    <label for="perPagePlayers" class="text-sm text-gray-700">{{ t('categoryDetail.perPage') }}</label>
                    <select id="perPagePlayers" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                      <option :value="75">75</option>
                      <option :value="100">100</option>
                    </select>
                  </div>
                  <div class="flex items-center gap-1">
                    <button
                      @click="prevPage"
                      :disabled="currentPage === 1"
                      class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &laquo;
                    </button>
                    <button
                      v-for="page in visiblePages"
                      :key="page"
                      @click="changePage(page)"
                      :class="[
                        'px-3 py-1 border-2 rounded text-sm',
                        page === currentPage
                          ? 'border-gray-900 bg-gray-900 text-white font-medium'
                          : 'border-gray-300 bg-white hover:bg-gray-50'
                      ]"
                    >
                      {{ page }}
                    </button>
                    <button
                      @click="nextPage"
                      :disabled="currentPage === lastPage"
                      class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Players -->
            <div v-else class="text-sm text-gray-500">
              {{ t('categoryDetail.noPlayers') }}
            </div>
          </div>
        </div>

        <!-- Events Section (if user has permission) -->
        <div v-if="canSeeEvents" class="mt-4">
          <h2 class="text-base font-semibold text-gray-900 mb-3">
            {{ t('events.title') }}
            <span v-if="displayDateFrom && displayDateTo" class="text-sm font-normal text-gray-600">
              ({{ t('events.inDays') }} {{ displayDateFrom }} - {{ displayDateTo }})
            </span>
          </h2>

          <!-- Date Filters -->
          <div class="mb-4 flex flex-wrap items-center gap-2">
            <label class="text-sm text-gray-700">{{ t('events.from') }}</label>
            <input type="date" v-model="dateFrom" class="px-3 py-1.5 text-sm border-2 border-gray-300 rounded">
            <label class="text-sm text-gray-700">{{ t('events.to') }}</label>
            <input type="date" v-model="dateTo" class="px-3 py-1.5 text-sm border-2 border-gray-300 rounded">
            <button @click="filterEvents" class="px-4 py-1.5 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700">
              {{ t('events.filter') }}
            </button>
          </div>

          <!-- Loading State for Events -->
          <div v-if="loadingEvents" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p class="mt-2 text-gray-600">{{ t('events.loadingEvents') }}</p>
          </div>

          <!-- Events Table (Desktop) -->
          <div v-if="!loadingEvents" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-x-auto overflow-y-visible">
            <table class="w-full text-sm">
              <thead class="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.event') }}</th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.place') }}</th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.categories') }}</th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.day') }}</th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700"><span class="lg:hidden">{{ t('events.date') }}</span><span class="hidden lg:inline">{{ t('events.start') }}</span></th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.end') }}</th>
                  <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.attendance') }}</th>
                  <th class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="(event, index) in events" :key="index" class="hover:bg-gray-50">
                  <td class="px-4 py-3 text-gray-900 font-medium">
                    <router-link
                      v-if="canShowEvent"
                      :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {{ event.title }}
                    </router-link>
                    <span v-else>{{ event.title }}</span>
                    <div class="lg:hidden text-xs text-gray-500 mt-0.5">{{ event.place }}</div>
                  </td>
                  <td class="px-4 py-3 text-gray-700 hidden lg:table-cell">{{ event.place }}</td>
                  <td class="px-4 py-3 hidden lg:table-cell">
                    <div v-if="event.categories && event.categories.length > 0" class="flex flex-wrap gap-1">
                      <template v-for="category in event.categories" :key="category.uuid">
                        <router-link
                          v-if="canClickCategory"
                          :to="`/categories/${category.uuid}`"
                          class="inline-block px-2 py-0.5 bg-gray-300 text-gray-900 text-[10px] rounded cursor-pointer hover:bg-gray-500 transition-colors"
                        >
                          {{ category.name }}
                        </router-link>
                        <span
                          v-else
                          class="inline-block px-2 py-0.5 bg-gray-300 text-gray-900 text-[10px] rounded"
                        >
                          {{ category.name }}
                        </span>
                      </template>
                    </div>
                    <span v-else class="text-gray-500 text-[10px]">-</span>
                  </td>
                  <td class="px-4 py-3 text-gray-700">{{ event.day }}</td>
                  <td class="px-4 py-3 text-gray-700">
                    {{ event.start }}
                    <div class="lg:hidden text-xs text-gray-500 mt-0.5">{{ event.end }}</div>
                  </td>
                  <td class="px-4 py-3 text-gray-700 hidden lg:table-cell">{{ event.end }}</td>
                  <td class="px-4 py-3">
                    <span v-if="event.has_attendance" class="text-gray-700">{{ event.attendance_present_count }}/{{ event.attendance_all_count }}</span>
                    <span v-else class="inline-block px-2 py-1 bg-red-50 text-red-700 text-xs font-medium rounded border border-red-300">{{ t('events.noAttendance') }}</span>
                  </td>
                  <td class="px-4 py-3 sticky right-0 bg-white text-right" style="position: relative;">
                    <div class="flex items-center gap-1 justify-end">
                      <button
                        v-if="canSeeActions"
                        @click="toggleDropdown(`desktop-${index}`, $event)"
                        class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                      >
                        {{ t('common.actions') }}
                      </button>
                      <div
                        v-if="canSeeActions"
                        :class="[
                          'custom-dropdown',
                          isLastThree(index) ? 'dropdown-top' : 'dropdown-bottom',
                          { 'show': openDropdownId === `desktop-${index}` }
                        ]"
                      >
                        <router-link v-if="canShowEvent" :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`">{{ t('common.view') }}</router-link>
                        <router-link v-if="canUpdateEvent" :to="`/events/${event.uuid}/edit?date=${event.start?.split(' ')[0]}`">{{ t('common.edit') }}</router-link>
                        <router-link v-if="canAttendanceStore && !event.has_attendance" :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`">{{ t('events.addAttendance') }}</router-link>
                        <router-link v-if="canAttendanceUpdate && event.has_attendance" :to="`/events/${event.uuid}/attendance/edit?date=${event.start?.split(' ')[0]}`">{{ t('events.editAttendance') }}</router-link>
                        <button v-if="canDeleteEvent" class="delete-action" @click="openDeleteModalFn(event)">{{ t('common.delete') }}</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Events Accordion (Mobile) -->
          <div v-if="!loadingEvents" class="md:hidden space-y-2">
            <div v-for="(event, index) in events" :key="index" class="bg-white border-2 border-gray-300 rounded shadow-sm">
              <!-- Accordion Header -->
              <div class="flex items-center justify-between p-3 cursor-pointer" @click="toggleAccordion(index)">
                <!-- Event info -->
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium truncate">
                    <router-link
                      v-if="canShowEvent"
                      :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`"
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                      @click.stop
                    >
                      {{ event.title }}
                    </router-link>
                    <span v-else>{{ event.title }}</span>
                  </div>
                  <div class="text-xs text-gray-600 mt-1">
                    <span class="font-medium">{{ getDayAbbreviation(event.day) }}</span>
                    <span class="ml-2">{{ event.start }}</span>
                    <span class="hidden min-[550px]:inline ml-1">- {{ event.end }}</span>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="flex items-center gap-2 ml-2">
                  <router-link
                    v-if="canAttendanceStore"
                    :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`"
                    @click.stop
                    :class="[
                      'px-3 py-1.5 rounded hover:opacity-80 border flex items-center justify-center',
                      event.has_attendance ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                      <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/>
                    </svg>
                  </router-link>
                  <div v-if="canSeeActions" style="position: relative;">
                    <button
                      @click="toggleDropdown(`mobile-${index}`, $event)"
                      class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                    >
                      {{ t('common.actions') }}
                    </button>
                    <div
                      :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === `mobile-${index}` }]"
                    >
                      <router-link v-if="canShowEvent" :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`">{{ t('common.view') }}</router-link>
                      <router-link v-if="canUpdateEvent" :to="`/events/${event.uuid}/edit?date=${event.start?.split(' ')[0]}`">{{ t('common.edit') }}</router-link>
                      <router-link v-if="canAttendanceStore && !event.has_attendance" :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`">{{ t('events.addAttendance') }}</router-link>
                      <router-link v-if="canAttendanceUpdate && event.has_attendance" :to="`/events/${event.uuid}/attendance/edit?date=${event.start?.split(' ')[0]}`">{{ t('events.editAttendance') }}</router-link>
                      <button v-if="canDeleteEvent" class="delete-action" @click="openDeleteModalFn(event)">{{ t('common.delete') }}</button>
                    </div>
                  </div>
                  <svg
                    :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionIds.has(index) }]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
              <!-- Accordion Content -->
              <div v-show="openAccordionIds.has(index)" class="border-t-2 border-gray-300 p-3">
                <div class="space-y-2 text-xs">
                  <div>
                    <span class="font-semibold text-gray-700">{{ t('events.place') }}:</span>
                    <span class="text-gray-600">{{ event.place }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">{{ t('events.attendance') }}:</span>
                    <span v-if="event.has_attendance" class="text-gray-600">{{ event.attendance_present_count }}/{{ event.attendance_all_count }}</span>
                    <span v-else class="inline-block px-2 py-1 bg-red-50 text-red-700 text-xs font-medium rounded border border-red-300">{{ t('events.noAttendance') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Events -->
          <div v-if="!loadingEvents && events.length === 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 text-sm text-gray-500">
            {{ t('events.noEvents') }}
          </div>
        </div>
      </template>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('events.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('events.deleteConfirmationMessage') }} "<span class="font-semibold">{{ eventToDelete?.title }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="deleting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="deleting"
          >
            {{ deleting ? t('events.deleting') : t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
